import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Fab,
  Zoom,
  Badge,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Stack
} from '@mui/material';
import styled from '@emotion/styled';
import { css } from '@emotion/react'
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ChatIcon from '@mui/icons-material/Chat';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';


import { formatGenericDateTime, formatPhoneNumber, sanitizeToGsm7 } from '../../utils/util';

import { BasicFlex } from '../../utils/Common';

import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';

import { sendMessage, getMessages } from './apiService';

import useIsMobile from '../../hooks/useIsMobile';

import useSnackbarStore from '../../store/snackbar';

import handleErrors from '../../utils/errorHandler';


// Emotion styled components
const MessageContainer = styled(Paper)`
  padding: 12px 16px;
  margin: 5px 0;
  max-width: ${props => props.$dialog ? '85%' : '70%'};
  align-self: ${props => props.direction === 'outbound' ? 'flex-end' : 'flex-start'};
  background-color: ${props => props.direction === 'outbound' ? props.theme.colors.tertiarySoften : '#ffffff'};
  color: ${props => props.direction === 'outbound' ? 'white' : props.theme.colors.text};
  border-radius: ${props => props.direction === 'outbound' ? '16px 16px 4px 16px' : '16px 16px 16px 4px'};
  border: ${props => props.direction === 'inbound' ? `1px solid ${props.theme.colors.border}` : 'none'};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  @media ${props => props.theme.breaks.down("md")} {
    max-width: 90%;
  }
`;

const MessagesWrapper = styled(Box)`
  height: 400px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${props => props.$dialog ? css/* closed */`
    height: auto;
    flex-grow: 1;
  ` : ""}
  /* background-color: #f9fafb; */
`;

const ReplyBox = styled(Box)`
  padding: 16px;
  border-top: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.theme.colors.darkBg};
`;

const HeaderBox = styled(Box)`
  padding: 12px 16px;
  background-color: #f9fafb;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const StyledChip = styled(Chip)`
  height: auto;
  font-size: 1.1rem;
  ${props => props.direction === 'outbound' && props.variant === 'outlined' && `
    background-color: rgba(255,255,255,0.2);
    color: white;
    border-color: rgba(255,255,255,0.5);
  `}
`;

const MessageText = styled.p`
  font-size: 1.6rem;
  line-height: 1.6;
`

const TimestampText = styled(Typography)`
  opacity: 0.8;
  font-size: 1.2rem;
  color: ${props => props.direction === 'outbound' ? 'rgba(255,255,255,0.8)' : '#6b7280'};
  min-width: 14.5rem;
`;

const StyledTextField = styled(TextField)`
  & .MuiOutlinedInput-root {
    border-radius: 12px;
  }
`;

const StyledButton = styled(Button)`
  border-radius: 12px;
  min-width: auto;
  /* padding: 0 24px; */
`;

const Container = styled(Box)`
  /* max-width: 600px; */
  margin: ${props => props.$dialog ? '0' : '0 auto'};
  /* border: 1px solid #e5e7eb; */
  border-radius: 8px;
  overflow: hidden;
  background-color: transparent;
  position: relative;

  ${props => props.$dialog ? css/* closed */`
    height: 100%;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  ` : ""}

  /* background-color: white; */
  /* box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); */
`;

const ScrollAnchor = styled(Box)`
  position: absolute;
  bottom: 90px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
`;

const MetaInfoBox = styled(BasicFlex)`
  flex-wrap: wrap;
  column-gap: 2rem;
  row-gap: .5rem;
`;  

const MetaChipsBox = styled(BasicFlex)`
  flex-wrap: wrap;
`

const SmsHeader = styled.span`
  font-weight: 500;
`

const PhoneNumber = styled.span`
  font-size: 1rem;
  color: ${props => props.theme.colors.mildText};
  font-weight: 600;
  font-style: italic;
`

// Sample data
const sampleMessages = [
  {
    customer: "John Doe",
    direction: "inbound",
    message_type: "conversation",
    delivery_type: "manual",
    user_id: null,
    timestamp: "2024-01-15T10:30:00Z",
    text: "Hi, I have a question about my appointment tomorrow."
  },
  {
    customer: "John Doe",
    direction: "outbound",
    message_type: "reminder",
    delivery_type: "automatic",
    user_id: "agent_123",
    timestamp: "2024-01-15T10:35:00Z",
    text: "Hello John! Your appointment is scheduled for tomorrow at 2:00 PM. Please arrive 15 minutes early."
  },
  {
    customer: "John Doe",
    direction: "inbound",
    message_type: "conversation",
    delivery_type: "manual",
    user_id: null,
    timestamp: "2024-01-15T10:40:00Z",
    text: "Can I reschedule it to Thursday instead?"
  },
  {
    customer: "John Doe",
    direction: "outbound",
    message_type: "notification",
    delivery_type: "automatic",
    user_id: "system",
    timestamp: "2024-01-15T10:45:00Z",
    text: "Your appointment has been rescheduled to Thursday at 2:00 PM. You'll receive a confirmation shortly."
  }
];

const getMessageTypeIcon = (messageType) => {
    switch (messageType) {
      case 'notification':
        return <NotificationsIcon sx={{ fontSize: 14 }} />;
      case 'reminder':
        return <ScheduleIcon sx={{ fontSize: 14 }} />;
      case 'conversation':
        return <ChatIcon sx={{ fontSize: 14 }} />;
      default:
        return <ChatIcon sx={{ fontSize: 14 }} />;
    }
  };

  const getMessageTypeColor = (messageType) => {
    switch (messageType) {
      case 'notification':
        return 'info';
      case 'reminder':
        return 'warning';
      case 'conversation':
        return 'success';
      default:
        return 'default';
    }
  };

const MessageBox = ({message, dialog}) => {
  const { isMobile } = useIsMobile();
  return (
    <MessageContainer
      $dialog={dialog}
      direction={message.direction}
      elevation={0}
    >
      <BasicFlex dir="column" gap="1rem">
        {/* Message content */}
        <MessageText>
          {message.text}
        </MessageText>
        
        {/* Message metadata */}
        <MetaInfoBox dir={isMobile ? "column" : "row"}>
          <MetaChipsBox justify="flex-start" gap=".6rem">
            <Chip
              icon={getMessageTypeIcon(message.message_type)}
              label={message.message_type}
              // size="small"
              color={getMessageTypeColor(message.message_type)}
              sx={{ height: 22, fontSize: '1.2rem' }}
            />
            
            <StyledChip
              label={message.direction}
              size="small"
              variant="outlined"
              direction={message.direction}
            />
            
            <StyledChip
              label={message.username ? `@${message.username}` : 'System'}
              size="small"
              variant="outlined"
              direction={message.direction}
            />
          </MetaChipsBox>
          
          <TimestampText variant="caption" direction={message.direction}>
            {formatGenericDateTime(message.timestamp)}
          </TimestampText>
        </MetaInfoBox>
      </BasicFlex>
    </MessageContainer>
  )
}

// Above this count, the initial scroll snaps instead of animating
const SCROLL_ANIMATION_MAX_MESSAGES = 5;
// How close to the bottom counts as "following the conversation"
const NEAR_BOTTOM_THRESHOLD = 150;
// Roughly how long a smooth scroll takes; scroll events are ignored until then
const SMOOTH_SCROLL_SETTLE_MS = 500;
const INSTANT_SCROLL_SETTLE_MS = 50;

const SMSMessagesComponent = ({ includeHeader = true, resource, resource_id, customer_id, customer_name, customer_phone, dialog = false }) => {
  const [replyText, setReplyText] = useState('');

  // monitor scroll position and behavior
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [newMessageCount, setNewMessageCount] = useState(0);

  const scrollContainerRef = useRef(null); // Ref for the wrapper
  const messagesEndRef = useRef(null);

  const isAtBottomRef = useRef(true);       // live scroll position, read by the messages effect
  const didInitRef = useRef(false);         // has the initial scroll-to-bottom run?
  const lastSeenIdRef = useRef(null);       // id of the newest message we've accounted for
  const forceScrollRef = useRef(false);     // set when we send, so our own message always scrolls
  const isAutoScrollingRef = useRef(false); // suppress handleScroll during programmatic scrolls
  const autoScrollTimerRef = useRef(null);

  const queryClient = useQueryClient();

  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

  const { data: messages } = useQuery({
    queryKey: ['messages', customer_id, resource, resource_id],
    queryFn: () => getMessages(customer_id, resource, resource_id),
    refetchInterval: 10000 // 10 seconds
  })

  const { mutate } = useMutation({
    mutationFn: sendMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', customer_id] });
    },
    onError: (error) => {
      // Don't let a failed send hijack the scroll on the next inbound message
      forceScrollRef.current = false;
      showSnackbar(handleErrors(error), 'error');
    },
  })

  const scrollToBottom = useCallback((behavior = "smooth") => {
    const container = scrollContainerRef.current;

    // Programmatic scrolling fires scroll events on the way down; ignore them
    // so handleScroll doesn't re-show the button mid-animation.
    isAutoScrollingRef.current = true;
    clearTimeout(autoScrollTimerRef.current);

    if (behavior === "auto" && container) {
      // Jump straight to the bottom without touching ancestor scroll positions
      container.scrollTop = container.scrollHeight;
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior });
    }

    // Smooth scroll has no widely-supported completion event, so release on a timer
    autoScrollTimerRef.current = setTimeout(
      () => { isAutoScrollingRef.current = false; },
      behavior === "auto" ? INSTANT_SCROLL_SETTLE_MS : SMOOTH_SCROLL_SETTLE_MS
    );

    isAtBottomRef.current = true;
    setShowScrollButton(false);
    setNewMessageCount(0);
  }, []);

  const handleSendReply = () => {
    if (replyText.trim()) {
      forceScrollRef.current = true;
      mutate({ text: replyText, resource, resource_id, customer_id });
      setReplyText('');
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendReply();
    }
  };

  const handleScroll = () => {
    if (isAutoScrollingRef.current) return;
    if (!scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;

    // Calculate how far we are from the bottom
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const atBottom = distanceFromBottom <= NEAR_BOTTOM_THRESHOLD;

    isAtBottomRef.current = atBottom;
    setShowScrollButton(!atBottom);

    // Scrolling back down manually dismisses the new-message badge
    if (atBottom) setNewMessageCount(0);
  };

  // Reset scroll bookkeeping when the conversation itself changes
  useEffect(() => {
    didInitRef.current = false;
    lastSeenIdRef.current = null;
    forceScrollRef.current = false;
    isAtBottomRef.current = true;
    setNewMessageCount(0);
    setShowScrollButton(false);
  }, [customer_id, resource, resource_id]);

  useEffect(() => () => clearTimeout(autoScrollTimerRef.current), []);

  useEffect(() => {
    if (!messages) return;

    const lastId = messages[messages.length - 1]?.id ?? null;

    // First load: park at the bottom, snapping if the thread is long
    if (!didInitRef.current) {
      didInitRef.current = true;
      lastSeenIdRef.current = lastId;
      scrollToBottom(messages.length > SCROLL_ANIMATION_MAX_MESSAGES ? "auto" : "smooth");
      return;
    }

    // Newest message unchanged means nothing arrived (edits/deletes don't scroll)
    if (lastId === lastSeenIdRef.current) return;

    // Count everything after the last message we accounted for. -1 means that
    // message is gone from the list, so treat the whole thread as unseen.
    const lastSeenIndex = messages.findIndex(m => m.id === lastSeenIdRef.current);
    const added = lastSeenIndex === -1
      ? messages.length
      : messages.length - lastSeenIndex - 1;

    lastSeenIdRef.current = lastId;

    if (added <= 0) return;

    // Our own outgoing message, or the user is already following along
    if (forceScrollRef.current || isAtBottomRef.current) {
      forceScrollRef.current = false;
      scrollToBottom("smooth");
      return;
    }

    // Scrolled up: leave the viewport alone and badge the button instead
    setNewMessageCount(prev => prev + added);
  }, [messages, scrollToBottom]);

  const hasNewMessages = newMessageCount > 0;

  return (
    <Container $dialog={dialog}>
      {/* Header */}
      {includeHeader ? <HeaderBox>
        <BasicFlex gap="1rem" justify="flex-start">
          <PersonIcon />
          <BasicFlex dir="column" justify="flex-start" gap="0.2rem">
            <SmsHeader>SMS - {customer_name}</SmsHeader>
            <PhoneNumber>{formatPhoneNumber(customer_phone)}</PhoneNumber>
          </BasicFlex>
        </BasicFlex>
      </HeaderBox> : null}

      {/* Messages */}
      <MessagesWrapper 
        $dialog={dialog}
        ref={scrollContainerRef} 
        onScroll={handleScroll}
      >
        {messages?.map((message, index) => (
          <MessageBox key={message.id ?? index} message={message} dialog={dialog} />
        ))}
        <div ref={messagesEndRef} />
      </MessagesWrapper>

      {/* Floating scroll / new-message button */}
      <Zoom in={showScrollButton || hasNewMessages}>
        <ScrollAnchor>
          <Badge
            badgeContent={newMessageCount}
            color="error"
            overlap="circular"
            invisible={!hasNewMessages}
          >
            <Fab
              color={hasNewMessages ? "secondary" : "primary"}
              size={hasNewMessages ? "medium" : "small"}
              variant={hasNewMessages ? "extended" : "circular"}
              onClick={() => scrollToBottom("smooth")}
              aria-label={hasNewMessages ? `${newMessageCount} new messages` : 'Scroll to latest'}
              sx={{ opacity: 0.95, whiteSpace: 'nowrap' }}
            >
              <KeyboardArrowDownIcon sx={{ mr: hasNewMessages ? 0.5 : 0 }} />
              {hasNewMessages
                ? `${newMessageCount} new message${newMessageCount > 1 ? 's' : ''}`
                : null}
            </Fab>
          </Badge>
        </ScrollAnchor>
      </Zoom>

      {/* Reply box */}
      <ReplyBox>
        <Stack direction="row" spacing={2} alignItems="flex-end">
          <StyledTextField
            fullWidth
            multiline
            maxRows={3}
            placeholder="Type your reply..."
            value={replyText}
            onChange={(e) => setReplyText(sanitizeToGsm7(e.target.value))}
            onKeyUp={handleKeyPress}
            size="small"
            slotProps={{input: {sx: {backgroundColor: '#fff'}}}}
          />
          <StyledButton
            variant="contained"
            endIcon={<SendIcon />}
            onClick={handleSendReply}
            disabled={!replyText.trim()}
          >
            Send
          </StyledButton>
        </Stack>
      </ReplyBox>
    </Container>
  );
};

export default SMSMessagesComponent;